import { useRef, useState } from 'react';
import { QrCode } from './QrCode.js';

/**
 * The passport QR affordance, shared by both portals' asset-detail header
 * cards (ops `PassportHeaderCard`, customer `QrGlyph`) — which previously each
 * carried their own hand-drawn <rect> mock of a QR code that encoded nothing.
 *
 * A real QR needs ~90px plus its quiet zone before a phone can lock onto it,
 * and neither header card has that room. So the header keeps a small mark and
 * the scannable code lives in a dialog: a 26px QR would render and never scan,
 * which is strictly worse than an icon that does not pretend to be one.
 *
 * The dialog is the native <dialog> element via `showModal()`, so the focus
 * trap, Esc-to-close, inertness of the page behind, and the top-layer stacking
 * are the browser's job rather than ours.
 *
 * NOTE — the URL this encodes is portal-internal and behind auth. Someone
 * scanning it who is not signed in reaches a login wall, which is the intended
 * behavior today: there is no public passport page. Building one is a separate
 * piece of work with its own threat model (opaque non-enumerable ids,
 * redaction rules for a stranger, rate limiting, revocation) and must not be
 * back-doored in here by pointing this QR at an unauthenticated route.
 */
export function PassportQrButton({
  passportUrl,
  serialLabel,
  onCopied,
}: {
  /** Absolute URL — the QR is scanned by a device that has no notion of the
   * current origin, so a relative path would be unusable. */
  passportUrl: string;
  /** Shown under the code so a human can tell which asset they are holding. */
  serialLabel: string;
  /** Fired after the URL reaches the clipboard, for the portal's own toast —
   * this component stays free of any toast library so both portals can use it. */
  onCopied?: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [copied, setCopied] = useState(false);

  function copy() {
    void navigator.clipboard.writeText(passportUrl).then(() => {
      setCopied(true);
      onCopied?.();
      // Local confirmation regardless of whether the host portal shows a toast.
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      <button
        type="button"
        onClick={() => dialogRef.current?.showModal()}
        aria-label="Show passport QR code"
        className="flex-none rounded-sm text-foreground hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        {/* Deliberately a scaled-down render of the REAL code, not a decorative
            glyph: the mark a user taps is the same code they get back, and it
            can never drift from the URL the way a hardcoded drawing did. */}
        <QrCode value={passportUrl} size={26} title={`Passport QR code for ${serialLabel}`} />
      </button>

      <dialog
        ref={dialogRef}
        onClick={(e) => {
          // Backdrop click closes. The dialog element itself fills only its own
          // box, so a click whose target IS the dialog landed on the backdrop.
          if (e.target === dialogRef.current) dialogRef.current?.close();
        }}
        className="rounded-lg border border-border bg-card p-0 text-foreground backdrop:bg-black/50"
      >
        <div className="flex w-[19rem] max-w-[90vw] flex-col items-center gap-3 p-6">
          <h2 className="text-sm font-semibold">Silicon Passport</h2>
          <QrCode value={passportUrl} size={200} title={`Passport QR code for ${serialLabel}`} />
          <p className="font-num text-xs text-muted-foreground">{serialLabel}</p>
          {/* Selectable text, not just the code: someone reading this on the
              same device it is displayed on cannot scan their own screen. */}
          <p className="w-full break-all rounded border border-border bg-muted px-2 py-1.5 text-center text-[11px] text-muted-foreground">
            {passportUrl}
          </p>
          <p className="text-center text-[11px] text-muted-foreground">
            Opens the passport in this portal &mdash; the viewer needs an account.
          </p>
          <div className="flex w-full gap-2">
            <button
              type="button"
              onClick={copy}
              className="flex-1 rounded-md border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted"
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <button
              type="button"
              onClick={() => dialogRef.current?.close()}
              className="flex-1 rounded-md border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted"
            >
              Close
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
}
